import { Box, ClickAwayListener, IconButton, LinearProgress } from "@mui/material";
import { cloneElement, ReactElement, useEffect, useState } from "react";
import { EditBase, Form, Labeled, useRecordContext, useResourceContext, useUpdate } from "react-admin";
import get from "lodash/get";
import set from "lodash/set";
import { LabeledBox } from "./LabeledBox";
import { Row } from "./Row";
import { useThemeColor } from "../hooks/useThemeColor";

interface LabeledAndEditableBaseProps {
  label: string;
  disableEdit?: boolean;
  children: ReactElement;
  source?: string;
  endIcon?: ReactElement;
  endIconClick?: () => void;
  endIconAriaLabel?: string;
}

interface LabeledAndEditableWithInputProps extends LabeledAndEditableBaseProps {
  renderInput: (args: { submit: (formData: { [k: string]: any }) => void; cancel: () => void }) => ReactElement;
}

interface LabeledAndEditableWithOnClickProps extends LabeledAndEditableBaseProps {
  onClick?: () => void;
}

type LabeledAndEditableProps = LabeledAndEditableWithInputProps | LabeledAndEditableWithOnClickProps;

export function LabeledAndEditable(props: LabeledAndEditableWithInputProps): ReactElement;
export function LabeledAndEditable(props: LabeledAndEditableWithOnClickProps): ReactElement;
export function LabeledAndEditable(props: LabeledAndEditableProps) {
  const { label, disableEdit, children: field, source: sourceProp, endIcon, endIconClick, endIconAriaLabel } = props;
  const onClick = "onClick" in props ? props.onClick : undefined;
  const renderInput = "renderInput" in props ? props.renderInput : undefined;
  const primaryColor = useThemeColor("primary");
  const [editMode, setEditMode] = useState(false);
  const toggleEditMode = () => setEditMode(!editMode);
  const resource = useResourceContext();
  const record = useRecordContext();
  const source = sourceProp ?? field.props.source;
  const [update, { isLoading, error, isError }] = useUpdate();
  useEffect(() => {
    if (isError) {
      console.error("Update failed", error);
      showErrorToast("Fehler beim Speichern.");
    }
  }, [isError, error]);

  useEffect(() => {
    if (!editMode) {
      return;
    }
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setEditMode(false);
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [editMode]);

  if (!record) {
    return null;
  }
  const { id } = record;
  const cancel = () => setEditMode(false);
  const isEditable = !disableEdit && !isLoading && (!!onClick || !!renderInput);
  const submit = (formData: { [k: string]: any }) => {
    setEditMode(false);
    if (typeof source === "string") {
      // lodash set & get methods handle nested fields.
      void update(resource, { id, data: set({}, source, get(formData, source)), previousData: record });
    } else {
      void update(resource, { id, data: formData, previousData: record });
    }
  };

  const handleEndIconClick = (e: React.MouseEvent) => {
    if (!endIconClick) {
      return;
    }
    e.stopPropagation();
    endIconClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) {
      return;
    }
    if (!isEditable || e.repeat) {
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      if (onClick) {
        onClick();
      } else if (renderInput) {
        toggleEditMode();
      }
    }
  };

  if (editMode && renderInput) {
    let input = renderInput({ submit, cancel });
    if (source) {
      input = cloneElement(input, {
        label: label,
        autoFocus: true,
        helperText: false,
        fullWidth: true,
        sx: { ...input.props.sx, width: "100%" },
        onKeyDown: ({ key }: KeyboardEvent) => {
          if (key === "Escape") {
            toggleEditMode();
          }
        },
        onBlur: toggleEditMode,
      });
    }
    if (input.type !== Labeled && input.type !== LabeledBox) {
      input = <Box mt="10px">{input}</Box>;
    }
    return (
      <EditBase>
        <Form className="LabeledAndEditable-Form" onSubmit={submit}>
          <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={cancel}>
            {input}
          </ClickAwayListener>
        </Form>
      </EditBase>
    );
  } else {
    return (
      <Labeled label={label}>
        {isLoading ? (
          <LinearProgress />
        ) : (
          <Row
            alignItems="center"
            justifyContent={endIcon ? "space-between" : "flex-start"}
            onClick={isEditable ? (onClick ?? toggleEditMode) : undefined}
            onKeyDown={handleKeyDown}
            tabIndex={isEditable ? 0 : undefined}
            role={isEditable ? "button" : undefined}
            aria-label={isEditable ? `${label} bearbeiten` : undefined}
            sx={
              isEditable
                ? {
                    cursor: "pointer",
                    borderRadius: "4px",
                    alignItems: "center",
                    paddingLeft: "4px",
                    marginLeft: "-4px",
                    // Added to make Empty Inputs hoverable (e.g. when no value was set yet)
                    minHeight: "30px",
                    "&:hover": {
                      backgroundColor: "action.hover",
                      "& .end-icon-button": {
                        opacity: 1,
                      },
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${primaryColor.main}`,
                      outlineOffset: "2px",
                      backgroundColor: "action.hover",
                      "& .end-icon-button": {
                        opacity: 1,
                      },
                    },
                  }
                : {
                    "&:hover .end-icon-button": {
                      opacity: 1,
                    },
                  }
            }
          >
            {field}
            {endIcon && (
              <IconButton
                size="small"
                aria-label={endIconAriaLabel}
                onClick={endIconClick ? handleEndIconClick : undefined}
                disabled={!endIconClick}
                className="end-icon-button"
                sx={{
                  opacity: 0,
                  transition: "opacity 0.15s",
                  ml: 0.5,
                  "&:focus-visible": {
                    opacity: 1,
                    backgroundColor: "transparent",
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                    opacity: 1,
                  },
                }}
                onMouseEnter={(e) => {
                  e.stopPropagation();
                }}
              >
                {endIcon}
              </IconButton>
            )}
          </Row>
        )}
      </Labeled>
    );
  }
}
